import { doc, getDoc, Firestore } from 'firebase/firestore';

/**
 * Propriétaire d'une annonce robe : userId (référence principale) ou ownerId (legacy).
 */

const UID_REGEX = /^[a-zA-Z0-9]{20,128}$/;

export function getDressOwnerUserId(data: Record<string, unknown>): string | null {
  const raw = data.userId != null ? data.userId : data.ownerId;
  if (typeof raw !== 'string') return null;
  const t = raw.trim();
  return t === '' ? null : t;
}

/** UID Firebase Auth typique : alphanumérique, longueur 20–128 (souvent 28). */
export function isValidDressOwnerUserId(userId: string | null | undefined): userId is string {
  if (!userId || typeof userId !== 'string') return false;
  const u = userId.trim();
  if (u.startsWith('test-') || u.includes('test-user')) return false;
  return UID_REGEX.test(u);
}

/** Compte utilisateur Firestore rattaché à une vraie inscription (email ou profil). */
export function isValidOwnerUserDocument(userData: Record<string, unknown> | undefined): boolean {
  if (!userData) return false;
  const email = userData.email;
  if (typeof email === 'string' && email.includes('@') && email.trim().length > 3) {
    return true;
  }
  const profile = userData.profile as Record<string, unknown> | undefined;
  if (profile && typeof profile.displayName === 'string' && profile.displayName.trim().length > 0) {
    return true;
  }
  if (typeof userData.displayName === 'string' && userData.displayName.trim().length > 0) {
    return true;
  }
  return false;
}

export type DressOwnerLinkResult = {
  valid: boolean;
  reason?:
    | 'invalid_or_missing_uid'
    | 'user_doc_missing'
    | 'user_profile_incomplete'
    | 'invalid_listing_document'
    | 'blocked_listing';
  ownerUid?: string;
};

/** Annonces retirées manuellement (doublons / tests / orphelines connues). */
const BLOCKED_LISTING_TITLES = new Set(['robe noir mini']);

export function isDressBlockedFromCatalog(dressData: Record<string, unknown>): boolean {
  const title = ((dressData.title as string) || (dressData.name as string) || '')
    .trim()
    .toLowerCase();
  return BLOCKED_LISTING_TITLES.has(title);
}

/** Annonce créée via le flux « Proposer une robe » (pas import/console). */
export function isValidDressListingDocument(dressData: Record<string, unknown>): boolean {
  if (dressData.hidden === true || dressData.status === 'removed') {
    return false;
  }
  if (dressData.createdAt == null) {
    return false;
  }
  const images = dressData.images;
  if (Array.isArray(images) && images.length > 0 && typeof images[0] === 'string') {
    return true;
  }
  return false;
}

export async function isDressLinkedToAccount(
  db: Firestore,
  dressData: Record<string, unknown>
): Promise<DressOwnerLinkResult> {
  if (isDressBlockedFromCatalog(dressData)) {
    return { valid: false, reason: 'blocked_listing' };
  }
  if (!isValidDressListingDocument(dressData)) {
    return { valid: false, reason: 'invalid_listing_document' };
  }
  const ownerUid = getDressOwnerUserId(dressData);
  if (!isValidDressOwnerUserId(ownerUid)) {
    return { valid: false, reason: 'invalid_or_missing_uid', ownerUid: ownerUid ?? undefined };
  }
  const userSnap = await getDoc(doc(db, 'users', ownerUid));
  if (!userSnap.exists()) {
    return { valid: false, reason: 'user_doc_missing', ownerUid };
  }
  if (!isValidOwnerUserDocument(userSnap.data() as Record<string, unknown>)) {
    return { valid: false, reason: 'user_profile_incomplete', ownerUid };
  }
  return { valid: true, ownerUid };
}
